import {Component, EventEmitter, inject, Input, Output, ViewChild, ViewEncapsulation} from '@angular/core';
import {Group, ImageEditorImageType, InspectionImageEntity} from "../../../data/types/groups/types";
import {DialogModule} from "primeng/dialog";
import {FormsModule} from "@angular/forms";
import {IconFieldModule} from "primeng/iconfield";
import {InputIconModule} from "primeng/inputicon";
import {InputTextModule} from "primeng/inputtext";
import {Make} from "../../../data/types/makes/types";
import {DropdownModule} from "primeng/dropdown";
import {ALL_MAKES_SELECT, DEFAULT_SELECTED_MAKE} from "../../../const";
import {InputSwitchModule} from "primeng/inputswitch";
import {Button} from "primeng/button";
import {CardModule} from "primeng/card";
import {CheckboxModule} from "primeng/checkbox";
import {DividerModule} from "primeng/divider";
import {ImageModule} from "primeng/image";
import {RoundPipe} from "../../../pipes/round.pipe";
import {TooltipModule} from "primeng/tooltip";
import {SjcApiService} from "../../../data/services/sjc-api.service";
import {ApproveSearchedImage, SearchQuery} from "../../../data/types/groups/params";
import {NgIf} from "@angular/common";
import {ProgressSpinnerModule} from "primeng/progressspinner";
import {MakeService} from "../../../data/services/make.service";
import {ApproveAllMakesBtnComponent} from "../approve-all-makes-btn/approve-all-makes-btn.component";
import {ImageService} from "../../../data/services/image.service";
import {HttpErrorResponse} from "@angular/common/http";
import {catchError, throwError} from "rxjs";
import {MessageService} from "primeng/api";
import {ImageEditorComponent} from "../image-editor/image-editor.component";

@Component({
    selector: 'app-groups-image-search',
    standalone: true,
    imports: [
        DialogModule,
        FormsModule,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        DropdownModule,
        InputSwitchModule,
        Button,
        CardModule,
        CheckboxModule,
        DividerModule,
        ImageModule,
        RoundPipe,
        TooltipModule,
        NgIf,
        ProgressSpinnerModule,
        ApproveAllMakesBtnComponent,
        ImageEditorComponent
    ]
    ,
    templateUrl: './groups-image-search.component.html',
    styleUrl: './groups-image-search.component.css',
    encapsulation: ViewEncapsulation.None
})
export class GroupsImageSearchComponent {
    @ViewChild(ImageEditorComponent) imageEditor!: ImageEditorComponent;

    @Output() onImageUploadEvent = new EventEmitter<boolean>()

    sjcService = inject(SjcApiService)
    makeService = inject(MakeService)
    imageService = inject(ImageService)
    messageService = inject(MessageService)

    approveAllMakesDialogVisible = false;

    @Input() group!: Group
    @Input() makes!: Make[]

    searchTypes: { name: string, value: number }[] = [{name: 'v1', value: 1}, {name: 'v2', value: 2}]

    loading: boolean = false;

    selectedMake: Make | undefined = DEFAULT_SELECTED_MAKE;
    groupSelectedMake: Make | undefined;
    searchText: string = ''

    noArrows: boolean = false;
    redArrows: boolean = true;
    orangeArrows: boolean = false;
    yellowArrows: boolean = false;
    greenArrows: boolean = false;
    imagesWithoutNotes: boolean = false;
    searchNotes: boolean = true;
    approvedOnly: boolean = false;
    offset: number = 0;
    limit: number = 32;
    selectedSearchType: { name: string, value: number } = {name: 'v1', value: 1};
    makeEnabled: boolean = true;
    protected readonly ALL_MAKES_SELECT = ALL_MAKES_SELECT;
    changedAtLeastOneApproved = false;
    previousQuery: string | undefined;
    noMoreResults = false;

    images: InspectionImageEntity[] = [];
    @Output() onApprovedImage = new EventEmitter<boolean>();

    reset() {
        this.images = [];
        this.loading = false;

        this.selectedMake = DEFAULT_SELECTED_MAKE;
        this.searchText = ''

        this.noArrows = false;
        this.redArrows = true;
        this.orangeArrows = false;
        this.yellowArrows = false;
        this.greenArrows = false;
        this.imagesWithoutNotes = false;
        this.searchNotes = true;
        this.approvedOnly = false;
        this.offset = 0;
        this.limit = 32;
        this.makeEnabled = true;
        this.noMoreResults = false;
        this.changedAtLeastOneApproved = false;
    }

    setGroup(group: Group) {
        this.group = group;
    }

    setGroupSelectedMake(selectedMake: Make) {
        this.groupSelectedMake = selectedMake;
        this.selectedMake = this.groupSelectedMake;
    }

    search() {
        this.offset = 0;
        this.images = [];
        this.loadMore();
    }

    protected createBody() {
        this.previousQuery = this.searchText;
        let makeId = 0;

        if (this.makeEnabled) {
            makeId = this.selectedMake?.id!
        }

        return {
            q: this.searchText,
            make_id: makeId,
            no_arrows: this.noArrows,
            red_arrows: this.redArrows,
            orange_arrows: this.orangeArrows,
            yellow_arrows: this.yellowArrows,
            green_arrows: this.greenArrows,
            no_notes: !this.imagesWithoutNotes,
            search_notes: this.searchNotes,
            approved_only: this.approvedOnly,
            offset: this.offset,
            limit: this.limit,
            version: this.selectedSearchType.value,
            sjc_group_id: this.group.id,
            group_selected_make_id: this.groupSelectedMake?.id!
        } as SearchQuery
    }

    loadMore() {
        if (this.noMoreResults && this.previousQuery == this.searchText) return

        this.loading = true
        const body = this.createBody();
        this.sjcService.search(body)
            .pipe(catchError((error) => this.handleError(error)))
            .subscribe((response) => {
                this.images = [...this.images, ...response.results[0]];
                this.limit = response.limit;
                this.offset += this.limit;
                this.loading = false;

                if (response.scroll_end) {
                    this.noMoreResults = true;
                }
            })
    }

    handleError(error: HttpErrorResponse) {
        this.loading = false;
        this.messageService.add({severity: 'error', summary: 'Error', detail: 'API returned error'});
        return throwError(() => new Error('Something bad happened; please try again later.'));
    }

    onScroll(event: any) {
        const scrollTop = event.target.scrollTop;
        const scrollHeight = event.target.scrollHeight;
        const clientHeight = event.target.clientHeight;

        if (scrollTop + clientHeight >= scrollHeight - 10) {
            if (!this.loading) {
                this.loadMore();
            }
        }
    }

    onChangeCheckbox(image: InspectionImageEntity, event: any) {
        const body: ApproveSearchedImage = {
            make_id: this.groupSelectedMake?.id!,
            image_id: image.sjc_inspection_image_id!,
            sjc_group_id: this.group.id,
            value: event.checked
        }

        this.changedAtLeastOneApproved = true;
        this.imageService.approveSearchImage(body);
    }

    onVisibilityApproveDialogChange($event: boolean) {
        this.approveAllMakesDialogVisible = $event
    }

    setSearchTextValue(value: string): void {
        this.searchText = value;
    }

    clearSearch() {
        this.searchText = '';
    }

    onMakesClear() {
        this.selectedMake = ALL_MAKES_SELECT;
    }

    approveAllMakes(image: InspectionImageEntity, group: Group) {
        this.makeService.setImageToApprove(
            image.sjc_inspection_image_id,
            group.id,
            undefined,
            this.selectedMake?.id!
        );
        this.approveAllMakesDialogVisible = true;
    }

    onDialogClose() {
        if (this.changedAtLeastOneApproved) {
            this.onApprovedImage.emit(true);
        }
    }

    openEditImage(image: InspectionImageEntity) {
        this.imageEditor.setImageType(ImageEditorImageType.SEARCH_IMAGE);
        this.imageEditor.setImage(image);
        this.imageEditor.setGroup(this.group)
        this.imageEditor.setGroupSelectedMake(this.groupSelectedMake!)
    }
}

